import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import * as maplibregl from "maplibre-gl";
import { ArrowRight, MapPinned, X } from "lucide-react";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Attribution, fitTo, hasCoords, pinElement, useMapLibre } from "@/components/maps/RouteMap";
import { fmtDate } from "@/lib/format";
import { cn } from "@/lib/utils";

const STATUS_TONE = { ny: "info", i_arbeid: "warning", utfort: "success", avlyst: "neutral" };

/** Oversiktskart: leveringsstadene til ein liste ordrar, farga etter status. Trykk på ein pin for detaljar. */
export const OverviewMap = ({ orders = [], className, heightClassName = "h-64 md:h-80", testId = "overview-map" }) => {
  const containerRef = useRef(null);
  const markerRefs = useRef([]);
  const [selected, setSelected] = useState(null);
  const pins = useMemo(
    () => orders.filter((o) => hasCoords(o.logistics?.delivery)).map((o) => ({ order: o, lat: o.logistics.delivery.lat, lng: o.logistics.delivery.lng })),
    [orders],
  );
  const hasAny = pins.length > 0;
  const map = useMapLibre(containerRef, { interactive: true, enabled: hasAny });
  const key = JSON.stringify(pins.map((p) => [p.order.id, p.order.status, p.lat, p.lng]));

  useEffect(() => {
    if (!map) return;
    markerRefs.current.forEach((m) => m.remove());
    markerRefs.current = pins.map(({ order, lat, lng }) => {
      const el = pinElement("", STATUS_TONE[order.status] || "neutral", 16);
      el.title = `${order.order_number} · ${order.title}`;
      el.style.cursor = "pointer";
      el.setAttribute("data-testid", `${testId}-pin`);
      el.addEventListener("click", (e) => {
        e.stopPropagation();
        setSelected(order);
      });
      return new maplibregl.Marker({ element: el, anchor: "center" }).setLngLat([lng, lat]).addTo(map);
    });
    fitTo(map, pins.map((p) => [p.lat, p.lng]), { padding: 48, maxZoom: 13 });
    const clear = () => setSelected(null);
    map.on("click", clear);
    return () => map.off("click", clear);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, key]);

  useEffect(() => {
    if (selected && !orders.some((o) => o.id === selected.id)) setSelected(null);
  }, [orders, selected]);

  if (!hasAny) {
    return (
      <div className={cn("flex flex-col items-center justify-center rounded-lg border border-dashed border-border bg-card text-center", heightClassName, className)} data-testid={`${testId}-empty`}>
        <MapPinned className="mb-2 h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
        <p className="text-sm text-muted-foreground">Ingen ordrar med stadfesta adresse i utvalet.</p>
      </div>
    );
  }
  const present = ["ny", "i_arbeid", "utfort", "avlyst"].filter((s) => pins.some((p) => p.order.status === s));
  return (
    <div className={cn("relative overflow-hidden rounded-lg border border-border bg-muted", heightClassName, className)} data-testid={testId}>
      <div ref={containerRef} className="h-full w-full" />
      <Attribution />
      <div className="pointer-events-none absolute left-2 top-2 z-10 flex flex-wrap gap-1.5" data-testid={`${testId}-legend`}>
        {present.map((s) => <StatusBadge key={s} status={s} variant="outline" />)}
      </div>
      {selected ? (
        <div className="absolute inset-x-2 bottom-2 z-10 rounded-lg border border-border bg-card p-3 md:inset-x-auto md:left-2 md:w-[300px]" data-testid={`${testId}-selected`}>
          <button type="button" onClick={() => setSelected(null)} className="focus-ring absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground" aria-label="Lukk" data-testid={`${testId}-selected-close`}>
            <X className="h-4 w-4" />
          </button>
          <p className="flex items-center gap-2 pr-7 text-[11px] text-muted-foreground"><span className="num">{selected.order_number}</span><StatusBadge status={selected.status} /></p>
          <p className="mt-1 line-clamp-2 text-sm font-semibold leading-snug">{selected.title}</p>
          <p className="truncate text-xs text-muted-foreground">{selected.customer_name}</p>
          <p className="tabular mt-1 truncate text-xs text-muted-foreground">{fmtDate(selected.planned_date)}{selected.logistics?.planned_time ? ` kl. ${selected.logistics.planned_time}` : ""} · {selected.logistics.delivery.name || selected.logistics.delivery.address}</p>
          <Link to={`/ordrar/${selected.id}`} className="focus-ring mt-2 inline-flex h-9 items-center gap-1.5 rounded-md bg-foreground px-3 text-xs font-medium text-background hover:bg-brand-charcoal" data-testid={`${testId}-open`}>
            Opne ordre <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      ) : null}
    </div>
  );
};
